import React, { useEffect, useState } from 'react';
import {
  Control,
  FieldErrors,
  FormProvider,
  SubmitHandler,
  useForm,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { Cog } from 'lucide-react';

import { Accordion } from 'components/Accordion';
import { Button } from 'components/Button';
import { ToastAction } from 'components/Toast';
import { RHFTextInput } from 'components/input/RHFTextInput';
import { Grid } from 'components/layout';
import ButtonLoader from 'components/loading/ButtonLoader';
import { Typography } from 'components/typography';
import { AvailableTabs } from 'constants/SidebarTabs';
import { ExtendedFormProvider } from 'contexts/extendedFormContext';
import { SidebarStateContext } from 'contexts/sidebarStateContext';
import useFetchCarrierEmailTemplates, {
  CarrierEmailTemplatesResponse,
} from 'hooks/useFetchEmailTemplates';
import { useLoadInvalidation } from 'hooks/useLoadContext';
import { useServiceFeatures } from 'hooks/useServiceContext';
import useTMSContext from 'hooks/useTMSContext';
import { useToast } from 'hooks/useToaster';
import { updateCarrierCheckInSOPs } from 'lib/api/updateCarrierCheckInSOPs';
import { EmailMode } from 'types/EmailMode';
import { EmailTemplate } from 'types/EmailTemplates';
import { TemplateType } from 'types/EmailTemplates';
import { LoadDateValue, NormalizedLoad } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import ButtonText from 'types/enums/ButtonText';
import { ButtonNamePosthog } from 'types/enums/CustomPosthogEvents';
import { TMS } from 'types/enums/Integrations';
import { sanitizeEmails } from 'utils/emails';
import { nl2br } from 'utils/getJoditEditorHelpers';

import { LoadSectionAccordionItem } from '../LoadInformation/Components';
import CarrierCheckInDetailsForm from './CarrierCheckInDetailsForm';
import { EmailsToScheduleSummary } from './EmailsToScheduleSummary';
import {
  CarrierCheckInInputs,
  CarrierCheckInTextInputProps,
  CheckInName,
  TimeUnits,
} from './types';
import { formatNormalizedTimestamp, hasAnyCheckIn } from './util';

dayjs.extend(utc);
dayjs.extend(timezone);

type CarrierCheckInSectionProps = {
  normalizedLoad: NormalizedLoad;
  setTab: React.Dispatch<React.SetStateAction<AvailableTabs>>;
};

export const CarrierSOPTextInput = (props: CarrierCheckInTextInputProps) => (
  <RHFTextInput {...props} />
);

export default function CarrierCheckInSection({
  normalizedLoad: load,
  setTab,
}: CarrierCheckInSectionProps) {
  const invalidateLoad = useLoadInvalidation();
  const { toast } = useToast();
  const { tmsName } = useTMSContext();
  const {
    serviceFeaturesEnabled: { isPODCollectionEnabled },
  } = useServiceFeatures();
  const {
    currentState: { inboxEmailAddress },
  } = React.useContext(SidebarStateContext);

  const [loading, setLoading] = useState(false);
  const [activeTabs, setActiveTabs] = useState<string[]>([
    'updateCarrierCheckInSOPs',
  ]);

  const { emailTemplates, isLoading } = useFetchCarrierEmailTemplates(
    load.ID,
    TemplateType.CARRIER
  );

  const { pickupTimestamp, dropoffTimestamp } = resolveApptTimes(load, tmsName);

  const pickupStartTime = dayjs(pickupTimestamp);
  const consigneeStartTime = dayjs(dropoffTimestamp);

  const pickup = formatNormalizedTimestamp(
    dayjs(pickupTimestamp),
    'MM/DD HH:mm',
    load.pickup.timezone
  );
  const dropoff = formatNormalizedTimestamp(
    dayjs(dropoffTimestamp),
    'MM/DD HH:mm',
    load.consignee.timezone
  );

  const isPickupValid = pickupStartTime.isValid();
  const isDropoffValid = consigneeStartTime.isValid();
  const isPickupInPast = pickupStartTime.isBefore(dayjs());
  const isDropoffInPast = consigneeStartTime.isBefore(dayjs());

  // Disable pickup milestones when the pickup appointment is older than a month
  const isPickupDisabled = pickupStartTime.isBefore(
    dayjs().subtract(1, 'month')
  );
  // Disable dropoff milestones when the dropoff appointment is older than a month
  const isDropoffDisabled = consigneeStartTime.isBefore(
    dayjs().subtract(1, 'month')
  );
  // Disable in-transit milestone when the dropoff appointment is older than a week
  const isInTransitDisabled = consigneeStartTime.isBefore(
    dayjs().subtract(1, 'week')
  );

  const generateDefaultValues = (
    templates: Maybe<CarrierEmailTemplatesResponse>
  ) => {
    const createDefaultDetails = (template: Undef<EmailTemplate>) => {
      return {
        checkIn: false,
        emailMode: EmailMode.New,
        from: inboxEmailAddress || '',
        cc: template?.cc ? sanitizeEmails([...template.cc]) : [],
        recipients: load.carrier.email ? [load.carrier.email] : [],
        subject: template?.subject ?? '',
        emailDraft: template?.body ? nl2br(template.body) : '',
      };
    };

    const defaultDispatchDetails = {
      ...createDefaultDetails(templates?.dispatchTemplate),
      duration: 0,
      timeUnit: TimeUnits.Minutes,
    };
    const defaultPickupDetails = {
      ...createDefaultDetails(templates?.pickupTemplate),
      duration: 0,
      timeUnit: TimeUnits.Minutes,
    };
    const defaultAfterPickupDetails = {
      ...createDefaultDetails(templates?.afterPickupTemplate),
      duration: 2,
      timeUnit: TimeUnits.Hours,
    };
    const defaultInTransitDetails = {
      ...createDefaultDetails(templates?.inTransitTemplate),
      frequency: 12,
      timeUnit: TimeUnits.Hours,
    };
    const defaultDropoffDetails = {
      ...createDefaultDetails(templates?.dropoffTemplate),
      duration: 0,
      timeUnit: TimeUnits.Minutes,
    };
    const defaultAfterDropoffDetails = {
      ...createDefaultDetails(templates?.afterDropoffTemplate),
      duration: 2,
      timeUnit: TimeUnits.Hours,
    };
    const defaultPODCollectionDetails = {
      ...createDefaultDetails(templates?.podCollectionTemplate),
    };

    return {
      freightTrackingId: load.freightTrackingID,
      dispatch: defaultDispatchDetails,
      pickup: defaultPickupDetails,
      afterPickup: defaultAfterPickupDetails,
      inTransit: defaultInTransitDetails,
      dropoff: defaultDropoffDetails,
      afterDropoff: defaultAfterDropoffDetails,
      podCollection: defaultPODCollectionDetails,
    };
  };

  const formMethods = useForm<CarrierCheckInInputs>({
    defaultValues: generateDefaultValues(emailTemplates),
  });
  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = formMethods;
  const carrierCheckInControl = control as unknown as Control<any>;

  useEffect(() => {
    if (!isLoading) {
      formMethods.reset(generateDefaultValues(emailTemplates));
    }
  }, [emailTemplates, formMethods]);

  const onSubmitUpdateSOPs: SubmitHandler<CarrierCheckInInputs> = async (
    data
  ) => {
    if (!hasAnyCheckIn(data)) {
      toast({
        description: 'No emails configured.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    const now = dayjs();

    const { pickupTimestamp, dropoffTimestamp } = resolveApptTimes(
      load,
      tmsName
    );

    const pickupApptTime = dayjs(pickupTimestamp);
    const dropoffApptTime = dayjs(dropoffTimestamp);

    // For dispatch, the backend expects the base time (now) and will add the duration
    const dispatchSendTime = now.toDate();

    // For pickup/dropoff, backend expects the appointment time and will apply the offset
    // If appointment is in the past, send current time instead
    const pickupSendTime = isPickupInPast
      ? now.toDate()
      : pickupApptTime.toDate();
    const dropoffSendTime = isDropoffInPast
      ? now.toDate()
      : dropoffApptTime.toDate();

    const carrierCheckInData = {
      freightTrackingId: data.freightTrackingId,
      dispatch: { ...data.dispatch, sendTime: dispatchSendTime },
      pickup: { ...data.pickup, sendTime: pickupSendTime },
      afterPickup: { ...data.afterPickup, sendTime: pickupSendTime },
      inTransit: data.inTransit,
      dropoff: { ...data.dropoff, sendTime: dropoffSendTime },
      afterDropoff: { ...data.afterDropoff, sendTime: dropoffSendTime },
      podCollection: data.podCollection,
    };

    const res = await updateCarrierCheckInSOPs(load.ID!, carrierCheckInData);
    if (res.isOk()) {
      const { numEmailsScheduled } = res.value;
      toast({
        description: `Scheduled ${numEmailsScheduled} ${numEmailsScheduled === 1 ? 'email' : 'emails'} to be sent.`,
        variant: 'success',
        action: (
          <ToastAction
            altText={ButtonText.GoToOutboxTab}
            onClick={() => setTab(AvailableTabs.Outbox)}
          >
            {ButtonText.GoToOutboxTab}
          </ToastAction>
        ),
      });
    } else {
      toast({
        description: res.error.message,
        variant: 'destructive',
      });
    }

    invalidateLoad();
    setLoading(false);
  };

  const handleOnInvalid = (formErrors: FieldErrors<CarrierCheckInInputs>) => {
    if (Object.keys(formErrors).length > 0) {
      setError('root', {
        type: 'manual',
        message:
          'There are errors in the form. Please correct them before submitting.',
      });
    }
  };

  return (
    <Accordion type='multiple' value={activeTabs} onValueChange={setActiveTabs}>
      <LoadSectionAccordionItem
        label='Contact Carrier'
        icon={<Cog className='h-6 w-6' strokeWidth={1} />}
        name='configureCarrierCheckInSOPs'
        activeTabs={activeTabs}
      >
        <div>
          <ExtendedFormProvider aiDefaultValues={true}>
            <FormProvider {...formMethods}>
              <div>
                <form
                  onSubmit={handleSubmit(onSubmitUpdateSOPs, handleOnInvalid)}
                  className='grid gap-4 grid-cols-1 mx-0 w-full'
                >
                  <Grid gap='md' className='mb-4 w-full mx-0'>
                    <div>
                      <Typography
                        variant='h5'
                        className='text-neutral-900 font-medium'
                      >
                        Event Triggers
                      </Typography>
                      <Typography
                        variant='body-xs'
                        className='mt-1 text-neutral-600'
                      >
                        Select specific check-in points in the load process to
                        send scheduled emails.
                      </Typography>
                    </div>

                    <div>
                      <Typography variant='body-sm'>
                        <span className='font-medium'>Pickup:</span> &nbsp;
                        {pickup}
                      </Typography>
                    </div>

                    <CarrierCheckInDetailsForm
                      name={CheckInName.Dispatch}
                      label='Confirm dispatch'
                      control={carrierCheckInControl}
                      disabled={isPickupDisabled || !isPickupValid}
                      isApptInPast={isPickupInPast}
                      load={load}
                    />
                    <CarrierCheckInDetailsForm
                      name={CheckInName.Pickup}
                      label='At pickup appointment'
                      control={carrierCheckInControl}
                      disabled={isPickupDisabled || !isPickupValid}
                      isApptInPast={isPickupInPast}
                      load={load}
                    />
                    <CarrierCheckInDetailsForm
                      name={CheckInName.AfterPickup}
                      label='Confirm loaded'
                      control={carrierCheckInControl}
                      disabled={isPickupDisabled || !isPickupValid}
                      isApptInPast={isPickupInPast}
                      load={load}
                    />
                    <CarrierCheckInDetailsForm
                      name={CheckInName.InTransit}
                      label='In-Transit'
                      control={carrierCheckInControl}
                      disabled={isInTransitDisabled || !isDropoffValid}
                      isApptInPast={isDropoffInPast}
                      load={load}
                    />

                    <div>
                      <Typography variant='body-sm'>
                        <span className='font-medium'>Dropoff:</span> &nbsp;
                        {dropoff}
                      </Typography>
                    </div>

                    <CarrierCheckInDetailsForm
                      name={CheckInName.Dropoff}
                      label='At dropoff appointment'
                      control={carrierCheckInControl}
                      disabled={isDropoffDisabled || !isDropoffValid}
                      isApptInPast={isDropoffInPast}
                      load={load}
                    />
                    <CarrierCheckInDetailsForm
                      name={CheckInName.AfterDropoff}
                      label='Confirm unloaded'
                      control={carrierCheckInControl}
                      disabled={isDropoffDisabled || !isDropoffValid}
                      isApptInPast={isDropoffInPast}
                      load={load}
                    />
                    {isPODCollectionEnabled && (
                      <CarrierCheckInDetailsForm
                        name={CheckInName.PodCollection}
                        label='POD Collection'
                        control={carrierCheckInControl}
                        load={load}
                      />
                    )}
                  </Grid>

                  <ErrorMessage
                    errors={errors}
                    name={'root'}
                    render={({ message }: { message: string }) => (
                      <Typography
                        variant='body-xs'
                        align='center'
                        className='text-error-500'
                      >
                        {message}
                      </Typography>
                    )}
                  />

                  <EmailsToScheduleSummary
                    control={carrierCheckInControl}
                    load={load}
                    isPickupInPast={isPickupInPast}
                    isDropoffInPast={isDropoffInPast}
                  />

                  <Button
                    buttonNamePosthog={ButtonNamePosthog.ScheduleCarrierEmails}
                    type='submit'
                    className='w-full mt-2'
                    disabled={loading}
                    logProperties={{
                      loadID: load.ID,
                      freightTrackingID: load.freightTrackingID,
                      serviceID: load.serviceID,
                    }}
                  >
                    {loading ? (
                      <ButtonLoader />
                    ) : (
                      ButtonText.ScheduleCarrierEmails
                    )}
                  </Button>
                </form>
              </div>
            </FormProvider>
          </ExtendedFormProvider>
        </div>
      </LoadSectionAccordionItem>
    </Accordion>
  );
}

const resolveApptTimes = (load: NormalizedLoad, tmsName: string) => {
  let pickupTimestamp: Maybe<LoadDateValue> = load.pickup.apptStartTime;
  let dropoffTimestamp: Maybe<LoadDateValue> = load.consignee.apptStartTime;

  if (tmsName === TMS.Tai) {
    if (!pickupTimestamp) {
      pickupTimestamp = load.pickup.readyTime;
    }
    if (!dropoffTimestamp) {
      dropoffTimestamp = load.consignee.mustDeliver;
    }
  }

  return {
    pickupTimestamp,
    dropoffTimestamp,
  };
};
